import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Link,
  Button,
  CircularProgress,
  Divider,
  Checkbox,
  CreditCardIcon,
  AppleIcon,
  FormControlLabel,
} from "../../../common/MuiImports";
import {
  useCustomerData,
  useUpdateCustomerData,
  useAddFutureProduct,
} from "../hooks/useApiData";
import { Controller, set, SubmitHandler } from "react-hook-form";
import { useBoingoForm } from "../../../../hooks/useBoingoForm";
import { useBraintreeForm } from "../../../../hooks/useBraintreeForm";
import BraintreeHostedFieldsForm from "../../../common/BraintreeHostedFieldsForm";
import PaymentLogos from "../../../../assets/images/payment_logos.png";
import { CustomerDataType, PaymentUpdateProps } from "../types/interface";
import UpdateCustomerModal from "./modals/UpdateCustomerModal";
import { SELFCARE_EVENTS, ACTION_TYPES } from "../../../../utils/eventConstants";
import eventBus from "../../../../utils/eventBus";
import { getVenueDetailsFromUrl } from "../../../../utils/utils";
import { useVenueDetailsFromCloudStorage } from "../hooks/useApiData";

import { useNavigate } from "react-router-dom";

interface PaymentFormProps {
  payment_braintree: {
    device_data: string;
    nonce: string;
    payment_type: string;
    payment_token: string;
  };
  country_code: string;
  subscription_opt_in?: boolean;
  has_free_wifi_product?: boolean;
}

interface CurrentPaymentMethodFormProps {
  onPaymentUpdate?: () => void;
  showSaveChangesButtons?: boolean;
  showActionButtons?: boolean;
  selectedProducts?: {
    product_code: string | string[];
    additional_devices?: Array<{ product_code: string }>;
    isFreeBulk?: boolean;
    selected_duration?: string;
    plan_name?: string;
    original_price?: string;
    discount?: {
      promo_code: string;
      discounted_price?: string;
    };
  };
  selectedDevices?: string[];
  actionType?: ACTION_TYPES;
  venue_id?: string;
}

const CurrentPaymentMethodForm: React.FC<CurrentPaymentMethodFormProps> = ({
  onPaymentUpdate,
  showSaveChangesButtons = true,
  showActionButtons = true,
  selectedProducts,
  selectedDevices,
  actionType,
  venue_id,
}) => {
  const navigate = useNavigate();
  const { data: customerDataQuery, isLoading } = useCustomerData();
  const { data: venueDetailsQuery } = useVenueDetailsFromCloudStorage();
  const updateCustomerMutation = useUpdateCustomerData();
  const addFutureProductMutation = useAddFutureProduct();
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [updateError, setUpdateError] = useState<string | undefined>();
  const [isCompletingPurchase, setIsCompletingPurchase] = useState(false);
  const [smsOptIn, setSmsOptIn] = useState(false);
  const [subscriptionOptIn, setSubscriptionOptIn] = useState(false);
  const { control, handleSubmit, errors } = useBoingoForm<PaymentFormProps>();
  const { isPaymentValid, braintreeFormRef, handleValidityChange } =
    useBraintreeForm();

  useEffect(() => {
    if (
      customerDataQuery?.data?.status === "INACTIVE" ||
      !customerDataQuery?.data?.payment_braintree?.payment_token ||
      customerDataQuery?.data?.has_free_wifi_product
    ) {
      setShowPaymentForm(true);
    }
    if (customerDataQuery?.data?.payment_braintree?.payment_type.toLowerCase() !== "free" && !customerDataQuery?.data?.has_free_wifi_product) {
      setShowPaymentForm(false);
    }
  }, [
    customerDataQuery?.data?.status,
    customerDataQuery?.data?.payment_braintree?.payment_token,
    customerDataQuery?.data?.payment_braintree?.payment_type
  ]);

  const handleModalClose = () => {
    setIsUpdateModalOpen(false);
    if (!updateError) {
      setShowPaymentForm(false);
    }
  };

  const handleCompletePurchase = async () => {
    setIsCompletingPurchase(true);
    try {
      let productCodes: string[] = [];
      if (selectedProducts) {
        const internetProductCodes = Array.isArray(
          selectedProducts.product_code
        )
          ? selectedProducts.product_code.filter(
            (code) => !code.toLowerCase().includes("retention")
          )
          : selectedProducts.product_code.toLowerCase().includes("retention")
            ? []
            : [selectedProducts.product_code];
        const additionalDeviceCodes =
          selectedProducts.additional_devices?.[0]?.product_code
            ?.split(",")
            ?.map((code: string) => code.trim()) || [];
        productCodes = [...internetProductCodes, ...additionalDeviceCodes];
      } else if (customerDataQuery?.data?.product_codes && selectedDevices) {
        productCodes = [
          ...customerDataQuery.data.product_codes,
          ...selectedDevices,
        ];
      }

      const updatedData = {
        product_codes: productCodes,
        sms_opt_in: smsOptIn,
        subscription_opt_in: subscriptionOptIn,
        ...(selectedProducts?.discount?.promo_code && selectedProducts.selected_duration === "monthly" && customerDataQuery?.data?.has_free_wifi_product && {
          discount_code: selectedProducts.discount.promo_code,
        }),
        venue_id: customerDataQuery?.data?.venue_id,
      };

      if (actionType === ACTION_TYPES.ADD_ANOTHER_ACTION) {
        await addFutureProductMutation.mutateAsync(
          updatedData as CustomerDataType
        );
      } else {
        const venueId = venueDetailsQuery?.data?.venue_details?.venue_id;
        try {
          const response = await updateCustomerMutation.mutateAsync(
            updatedData as CustomerDataType
          );
          if (response && (Number(response.status) === 200 || Number(response.status) === 201)) {

            actionType === ACTION_TYPES.REACTIVATE_ACTION ?
              eventBus.dispatch(SELFCARE_EVENTS.REACTIVATE_PLAN_SUCCESS, {
                action_type: ACTION_TYPES.REACTIVATE_ACTION,
                product_codes: productCodes,
                venue_id: venueId,
                selected_products: selectedProducts,
              }) : 
              eventBus.dispatch(SELFCARE_EVENTS.CHANGE_PLAN_SUCCESS, {
                  action_type: ACTION_TYPES.CHANGE_PLAN_ACTION,
                  product_codes: productCodes,
                  venue_id: venueId,
                  selected_products: selectedProducts,
                })
            }
        } catch (changePlanError: any) {
          // Dispatch error event for change plan failure
          actionType === ACTION_TYPES.REACTIVATE_ACTION
            ? eventBus.dispatch(SELFCARE_EVENTS.REACTIVATE_PLAN_ERROR, {
                action_type: ACTION_TYPES.REACTIVATE_ACTION,
                product_codes: productCodes,
                venue_id: venueId,
                selected_products: selectedProducts,
              })
            : actionType === ACTION_TYPES.CHANGE_PLAN_ACTION &&
              eventBus.dispatch(SELFCARE_EVENTS.CHANGE_PLAN_ERROR, {
                action_type: ACTION_TYPES.CHANGE_PLAN_ACTION,
                product_codes: productCodes,
                venue_id: venueId,
                selected_products: selectedProducts,
              });
          throw changePlanError; // Re-throw to be caught by outer catch
        }
      }

      navigate("/");
    } catch (error: any) {
      console.error("Error in complete purchase:", error);
      
      setUpdateError(
        error?.message || "An error occurred while updating your profile."
      );
      setIsUpdateModalOpen(true);
    } finally {
      setIsCompletingPurchase(false);
      setIsSubmitting(false);
    }
  };

  const onSubmit: SubmitHandler<PaymentFormProps> = async (formData) => {
    if (customerDataQuery?.data) {
      if (isPaymentValid && braintreeFormRef.current) {
        setIsSubmitting(true);

        try {
          const { payload, deviceData } =
            await braintreeFormRef.current.tokenize();

          if (payload && payload.nonce) {
            const updatedData: PaymentUpdateProps = {
              payment_braintree: {
                nonce: payload.nonce,
                device_data: deviceData,
                payment_type: "Credit Card",
                country_code: formData?.country_code,
              },
            };

            updateCustomerMutation.mutate(updatedData as CustomerDataType, {
              onSuccess: () => {
                setUpdateError(undefined);
                setIsUpdateModalOpen(true);
                setIsSubmitting(false);
                if (onPaymentUpdate) {
                  onPaymentUpdate();
                }
              },
              onError: (error: any) => {
                setUpdateError(
                  error?.message ||
                  "An error occurred while updating your profile."
                );
                setIsUpdateModalOpen(true);
                setIsSubmitting(false);
              },
            });
          }
        } catch (error) {
          console.error("Error updating payment method:", error);
          setIsSubmitting(false);
        }
      }
    }
  };

  const getPaymentMethodDisplay = () => {
    if (
      customerDataQuery?.data?.status === "INACTIVE" &&
      !customerDataQuery?.data?.payment_braintree?.payment_token
    ) {
      return "Credit Card •••• •••• •••• ••••";
    }

    if (customerDataQuery?.data.payment_braintree?.credit_card_last_4) {
      return `${customerDataQuery?.data.payment_braintree?.payment_type} •••• •••• •••• ${customerDataQuery?.data.payment_braintree?.credit_card_last_4}`;
    }

    if (
      customerDataQuery?.data.payment_braintree?.payment_type === "Apple Pay"
    ) {
      return "Apple Pay";
    }

    return "None";
  };

  if (isLoading) {
    return <CircularProgress />;
  }

  return (
    <Box className="paymentMethod" data-test-id="current-payment-method-form">
      <Box
        sx={{
          display: "block",
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontFamily: "Figtree-Bold",
            fontSize: 17,
          }}
          data-test-id="current-payment-method-title"
        >
          Current Payment Method
        </Typography>
        <Box
          sx={{
            flexDirection: "row",
            alignItems: "center",
            gap: 1,
            marginY: 2,
            display: "flex",
          }}
        >
          {customerDataQuery?.data.payment_type === "Apple Pay" ? (
            <AppleIcon sx={{ fontSize: 30 }} />
          ) : (
            <CreditCardIcon sx={{ fontSize: 30 }} />
          )}
          <Typography
            variant="body1"
            data-test-id="current-payment-method-display"
          >
            {getPaymentMethodDisplay()}
          </Typography>
        </Box>

        {(customerDataQuery?.data?.payment_braintree?.payment_token || 
          (customerDataQuery?.data?.payment_braintree?.payment_type?.toLowerCase() !== "free" && 
           !customerDataQuery?.data?.has_free_wifi_product)) && (
          <Link
            component="button"
            onClick={() => setShowPaymentForm(!showPaymentForm)}
            sx={{
              color: "secondary.main",
              textDecoration: "none",
              cursor: "pointer",
              mt: 1,
              "&:hover": {
                textDecoration: "underline",
              },
            }}
            data-test-id="hide-show-payment-form-link"
          >
            {showPaymentForm ? "Hide Payment Form" : "Edit Payment Details"}
          </Link>
        )}
      </Box>

      {showPaymentForm && (
        <Box
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          noValidate
          sx={{ position: "relative" }}
          data-test-id="payment-form"
        >
          <Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                mt: 4,
              }}
            >
              <CreditCardIcon sx={{ fontSize: 30, marginRight: 1 }} />
              <Typography
                variant="h6"
                sx={{ fontFamily: "Figtree-Bold", fontSize: 17 }}
              >
                New Payment Method
              </Typography>
            </Box>
            <img
              src={PaymentLogos}
              alt="Payment Logos"
              height={30}
              style={{ marginTop: 8 }}
              data-test-id="payment-logos-image"
            />

            <BraintreeHostedFieldsForm
              ref={braintreeFormRef}
              control={control}
              onValidityChange={handleValidityChange}
            />
          </Box>
          {showSaveChangesButtons && (
            <Box
              sx={{ display: "flex", gap: 2, mt: 2 }}
              data-test-id="form-buttons-container"
            >
              <Button
                disableElevation
                variant="outlined"
                color="secondary"
                onClick={() => setShowPaymentForm(false)}
                sx={{ height: "40px", flex: 1 }}
              >
                Cancel
              </Button>
              <Button
                disableElevation
                type="submit"
                variant="contained"
                color="secondary"
                disabled={isSubmitting}
                sx={{ height: "40px", flex: 1 }}
                data-test-id="save-changes-button"
              >
                {isSubmitting ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CircularProgress size={20} color="inherit" />
                  </Box>
                ) : (
                  "Save Changes"
                )}
              </Button>
            </Box>
          )}
        </Box>
      )}

      {!showPaymentForm && showActionButtons && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            mt: 2,
          }}
        >
          <Divider />
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
            <Checkbox
              sx={{ mt: -0.5 }}
              checked={smsOptIn}
              onChange={(e) => setSmsOptIn(e.target.checked)}
              value="sms"
            />
            <Typography variant="body1" sx={{ flex: 1 }}>
              By checking this box, you consent to receive updates and marketing
              communications via SMS. You can change your preferences at any
              time.
            </Typography>
          </Box>
          {customerDataQuery?.data?.plan_type !== "free" && selectedProducts?.selected_duration === "monthly" && (
            <Controller
              name="subscription_opt_in"
              control={control}
              defaultValue={false}
              rules={{
                required: "You must agree to the renewal notice.",
              }}
              render={({ field }) => (
                <Box>
                  <FormControlLabel
                    control={
                      <Checkbox
                        {...field}
                        checked={field.value}
                        onChange={(e) => {
                          field.onChange(e.target.checked);
                          setSubscriptionOptIn(e.target.checked);
                        }}
                        data-test-id="subscribe-checkbox"
                        sx={{ mt: -0.5, ml: 1.4, mr: 0.9}}
                      />
                    }
                    label={
                      <Typography
                        variant="body1" sx={{ flex: 1, fontFamily:"Figtree-Bold"}}
                      >
                        {selectedProducts?.discount
                          ? `This subscription will renew monthly at $${selectedProducts?.original_price} after your first month at $${selectedProducts?.discount?.discounted_price}, until I cancel, and I can cancel any time.`
                          : `This subscription will renew monthly at ${selectedProducts?.original_price} until I cancel, and I can cancel any time.`}
                      </Typography>
                    }
                  />
                  {errors.subscription_opt_in && (
                    <Typography
                      variant="body2"
                      color="error"
                      sx={{ mt: 1, ml: 4 }}
                      data-test-id="subscription-opt-in-error"
                    >
                      {errors.subscription_opt_in.message}
                    </Typography>
                  )}
                </Box>
              )}
            />
          )}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: { xs: 0, md: 2 },
            }}
          >
            <Button
              variant="outlined"
              disableElevation
              color="secondary"
              onClick={() => navigate("/")}
              disabled={isCompletingPurchase}
              sx={{
                height: "40px",
                width: { xs: "100%", md: "200px" },
                my: { xs: 0, md: 2 },
                order: { xs: 2, md: 1 },
                "&.Mui-disabled": {
                  backgroundColor: "#ccc",
                  color: "#666",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              disableElevation
              variant="contained"
              color="secondary"
              onClick={handleCompletePurchase}
              disabled={
                isCompletingPurchase ||
                (customerDataQuery?.data?.plan_type !== "free" && 
                 selectedProducts?.selected_duration === "monthly" && 
                 !subscriptionOptIn)
              }
              data-test-id="current-payment-continue-button"
              sx={{
                height: "40px",
                width: { xs: "100%", md: "200px" },
                my: 2,
                order: { xs: 1, md: 2 },
                "&.Mui-disabled": {
                  backgroundColor: "#ccc",
                  color: "#666",
                },
              }}
            >
              {isCompletingPurchase ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={20} color="inherit" />
                </Box>
              ) : (
                selectedProducts?.selected_duration === "monthly" ?
                "Agree and subscribe" :
                "Complete Transaction"
              )}
            </Button>
          </Box>
        </Box>
      )}

      <UpdateCustomerModal
        open={isUpdateModalOpen}
        onClose={handleModalClose}
        isError={!!updateError}
        errorMessage={updateError}
      />
    </Box>
  );
};

export default CurrentPaymentMethodForm;
